import React, { FC } from 'react';
import {
  TypeImage,
  TypeImageFields,
  TypeLandingPageHeroFields,
  TypeVideoFields,
} from '@/core/generated-types';
import { Entry } from 'contentful';
import {
  getMediaComponent,
  isMuxVideo,
  isVideo,
} from '@/components/common/LandingPageHero/utils';
import { LuvImage } from '@/components/ui/LuvImage';
import { CONTENT_TYPE_LANDING_PAGE_HERO } from '@/core/constants';
import { UniversalCta } from '../../UniversalCta';
import Heading, {
  HeadingSize,
  HeadingTag,
} from '@/components/ui/Heading/Heading';

const TriptychMedia: FC<{
  media: Entry<TypeImageFields | TypeVideoFields>;
}> = ({ media }) => {
  let altText = '';
  let typeImage: TypeImage | undefined;

  const mediaIsVideo = isMuxVideo(media) || isVideo(media);
  if (!mediaIsVideo) {
    typeImage = media as TypeImage;
    altText = typeImage?.fields.altText ?? '';
  }

  return (
    <div className="mx-auto inline-block">
      {mediaIsVideo ? (
        getMediaComponent({
          media,
          classes: 'h-[240px] lg:h-[480px] w-[1096px]',
        })
      ) : (
        <LuvImage
          imageData={typeImage}
          alt={altText}
          height={480}
          width={1096}
        />
      )}
    </div>
  );
};

export const ContainedImage: FC<TypeLandingPageHeroFields> = ({
  entryName,
  media,
  subHeaderText,
  headerText,
  subtext,
  cta,
  textBelowCta,
  version,
}) => {
  const isTriptych =
    version === CONTENT_TYPE_LANDING_PAGE_HERO.CONTAINED_W_TRIPTYCH;

  return (
    <section
      data-testid={`landing-page-hero-${entryName}`}
      className={`mx-auto text-center ${isTriptych ? 'bg-teal px-4 py-6' : ''}`}
    >
      <div className="relative mb-6">
        {isTriptych ? (
          <TriptychMedia media={media} />
        ) : (
          <>
            <div className="border-solid border-white bg-teal lg:h-[448px] lg:border-b-18 lg:border-t-18"></div>
            <div className="left-1/2 top-0 mx-auto w-full max-w-[1096px] lg:absolute lg:-translate-x-1/2 lg:transform">
              {getMediaComponent({
                media,
                classes:
                  'flex h-[240px] items-center justify-center bg-cover bg-center lg:h-[448px] lg:rounded w-full',
              })}
            </div>
          </>
        )}
      </div>

      <div className="mx-auto w-full max-w-[860px] px-4 md:w-[80vw]">
        {subHeaderText && (
          <Heading
            tag={HeadingTag.H3}
            size={HeadingSize.H3}
            text={subHeaderText}
            align="center"
            classes="mb-3"
          />
        )}
        <Heading
          tag={HeadingTag.H1}
          size={HeadingSize.H1}
          classes="mb-2"
          text={headerText}
          hoverEffect
        />
        <div className="mx-auto text-h-x6 lg:w-1/2 lg:text-d-lg">{subtext}</div>
      </div>
      {cta && (
        <div className="mt-6 px-8">
          <UniversalCta link={cta} />
        </div>
      )}
      {textBelowCta && (
        <div className="my-4 text-base text-gray-600">{textBelowCta}</div>
      )}
    </section>
  );
};
